<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12">
          <v-card class="px-7 mt-4 pt-7">
            <v-row>
              <v-col cols="12">
                <SimplePageHeader
                  v-if="!isEdit"
                  title="Add Employee"
                ></SimplePageHeader>
                <SimplePageHeader
                  v-else
                  title="Edit Employee"
                >
                  <template v-slot:right-buttons>
                    <v-switch
                      v-model="active"
                      label="Active"
                      class="float-end"
                    ></v-switch>
                  </template>
                </SimplePageHeader>
              </v-col>
            </v-row>
            <v-row class="pt-3" v-if="!isEdit">
              <v-col cols="6" sm="3">
                <v-btn
                  block
                  min-height="70px"
                  color="primary"
                  style="font-size: 20px"
                  :outlined="newUserOutlined"
                  @click="newUserButtonClicked"
                >
                  New<br>User
                </v-btn>
              </v-col>
              <v-col cols="6" sm="3">
                <v-btn
                  block
                  min-height="70px"
                  color="primary"
                  style="font-size: 20px"
                  :outlined="existingUserOutlined"
                  @click="existingUserButtonClicked"
                >
                  Existing<br>User
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6" v-if="!isNewUser">
                <UsersDropDownList
                  :userId="userId"
                  :isRequired="true"
                  label="User"
                  :onlyActiveUsers="true"
                  @userIdChange="userIdChange"
                ></UsersDropDownList>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="First Name"
                  v-model="firstName"
                  :rules="[required('First Name')]"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Last Name"
                  v-model="lastName"
                  :rules="[required('Last Name')]"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <EmailInput
                  label="Login E-Mail"
                  :email-address="emailAddress"
                  :is-required="true"
                  :disabled="false"
                  @emailChange="(newValue) => this.emailAddress = newValue"
                ></EmailInput>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <EmailInput
                  label="Corporate E-Mail"
                  :email-address="corporateEmailAddress"
                  :is-required="false"
                  :disabled="false"
                  @emailChange="(newValue) => this.corporateEmailAddress = newValue"
                ></EmailInput>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <SecurityRolesDropDownList
                  :securityRoles="securityRoles"
                  :multiple="true"
                  :isRequired="true"
                  @securityRolesChange="(newValue) => this.securityRoles = newValue"
                ></SecurityRolesDropDownList>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-if="zohoIntegrationEnabled"
                  label="Zoho User Id"
                  v-model="zohoUserId"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <SimpleDatePicker
                  label="Start Date"
                  :value="startDate"
                  :isRequired="true"
                  @selected-date-change="(newValue) => this.startDate = newValue"
                ></SimpleDatePicker>
              </v-col>
              <v-col cols="12" sm="6">
                <SimpleDatePicker
                  label="End Date"
                  :value="endDate"
                  :isRequired="!active"
                  :clearable="true"
                  @selected-date-change="endDateChangeHandler"
                ></SimpleDatePicker>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <v-radio-group
                  class="my-0"
                  label="Employee Type"
                  v-model="employeeTypeId"
                  :rules="[isFilledOut()]"
                  @change="() => {this.corporateName = null; this.ein = null; this.$refs.form.resetValidation();}"
                >
                  <v-radio label="1099" :value="INDEPENDENT_CONTRACTOR"></v-radio>
                  <v-radio label="C2C" :value="CORP_TO_CORP"></v-radio>
                  <v-radio label="W2 no Benefits" :value="EMPLOYEE_NO_BENEFITS"></v-radio>
                  <v-radio label="W2 + Benefits" :value="EMPLOYEE_WITH_BENEFITS"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="6">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      label="Corp. Name"
                      v-model="corporateName"
                      validate-on-blur
                      :disabled="!isC2C"
                      :rules="[isCorporateNameRequired]"
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      label="EIN"
                      v-model="ein"
                      validate-on-blur
                      :disabled="!isC2C"
                      :rules="[isEINRequired]"
                      outlined
                      maxlength="10"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <FormattedNumberField
                  label="Pay Amount"
                  :value="payAmount"
                  :required="true"
                  prepend-inner-icon="attach_money"
                  @valueChange="(newValue) => this.payAmount = newValue"
                  @blur="setPayRateType"
                ></FormattedNumberField>
              </v-col>
              <v-col cols="12" sm="6">
                <PayRateTypesDropdownList
                  label="Pay Type"
                  :isRequired="true"
                  :payRateType="payTypeId"
                  @payRateTypeChangeHandler="(newValue) => this.payTypeId = newValue">
                </PayRateTypesDropdownList>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <PayFrequenciesDropdownList
                  :isRequired="true"
                  :payFrequency="payFrequencyId"
                  @payFrequencyChangeHandler="(newValue) => this.payFrequencyId = newValue">
                </PayFrequenciesDropdownList>
              </v-col>
              <v-col cols="12" sm="6">
                <FormattedNumberField
                  label="Hours Worked Monthly"
                  :value="workedHoursAverage"
                  :required="true"
                  @valueChange="(newValue) => this.workedHoursAverage = newValue"
                ></FormattedNumberField>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card class="pb-3">
            <v-card-text>
              <CommissionTiersCollector
                :commissionTiers="commissionTiers"
                @commissionTiersChange="(newValue) => this.commissionTiers = newValue"
              ></CommissionTiersCollector>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <ButtonsSubmitForm
        :isFormValid="isFormValid"
        @save="handleSaveEmployee"
        @cancel="cancel"
      >
      </ButtonsSubmitForm>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { staffRoute } from '@/lib/constants/routes'
import { ButtonsSubmitForm } from '@/components/buttons'
import { required } from '@/lib/formValidations'
import { SimplePageHeader } from '@/components/pageComponents'
import { EmailInput } from '@/components/emails'
import UsersDropDownList from '@/components/users/UsersDropDownList'
import { FormattedNumberField } from '@/components/shared/textFields'
import { PayRateTypesDropdownList } from '@/components/payRateTypes'
import {
  ANNUAL as ANNUALLY_PAID,
  HOURLY as HOURLY_PAID,
  MONTHLY as MONTHLY_PAID
} from '@/lib/constants/payRateTypes'
import { PayFrequenciesDropdownList } from '@/components/payFrequencies'
import { INDEPENDENT_CONTRACTOR, CORP_TO_CORP, EMPLOYEE_NO_BENEFITS, EMPLOYEE_WITH_BENEFITS } from '@/lib/constants/employeeTypes'
import { CommissionTiersCollector } from '@/components/commissionTiers'
import { SecurityRolesDropDownList } from '@/components/securityRoles'
import SimpleDatePicker from '@/components/shared/datePickers/SimpleDatePicker'

export default {
  components: {
    SimpleDatePicker,
    UsersDropDownList,
    ButtonsSubmitForm,
    SimplePageHeader,
    EmailInput,
    FormattedNumberField,
    PayRateTypesDropdownList,
    PayFrequenciesDropdownList,
    CommissionTiersCollector,
    SecurityRolesDropDownList
  },
  data () {
    return {
      isFormValid: false,
      NEW_USER: 1,
      EXISTING_USER: 2,
      userType: 1,
      newUserOutlined: false,
      existingUserOutlined: true,
      firstName: null,
      lastName: null,
      emailAddress: null,
      securityRoles: [],
      INDEPENDENT_CONTRACTOR,
      CORP_TO_CORP,
      EMPLOYEE_NO_BENEFITS,
      EMPLOYEE_WITH_BENEFITS
    }
  },
  computed: {
    ...mapFields('settings', ['payRateSettings', 'zohoIntegrationEnabled']),
    ...mapFields('staff', [
      'user',
      'userId',
      'commissionTiers',
      'employeeTypeId',
      'corporateName',
      'ein',
      'payAmount',
      'payTypeId',
      'payFrequencyId',
      'workedHoursAverage',
      'corporateEmailAddress',
      'active',
      'startDate',
      'endDate',
      'zohoUserId'
    ]),
    id: {
      get () {
        return this.$route.params.id || null
      }
    },
    isEdit: {
      get () {
        return !!this.id
      }
    },
    isNewUser () {
      return this.userType === this.NEW_USER
    },
    isC2C () {
      return this.employeeTypeId === CORP_TO_CORP
    }
  },
  methods: {
    ...mapActions('settings', ['getSettings']),
    ...mapActions('user', [
      'getUser'
    ]),
    ...mapActions('staff', [
      'getStaffEmployee',
      'saveStaffEmployee',
      'updateStaffEmployee',
      'clearStaffEmployee'
    ]),
    ...mapActions('commissionsTiers', ['getCommissionsTiers']),
    required,
    setPayRateType () {
      if (this.payAmount && !this.payTypeId) {
        if (this.payAmount < 500) {
          this.payTypeId = HOURLY_PAID
        } else if (this.payAmount < 19999) {
          this.payTypeId = MONTHLY_PAID
        } else if (this.payAmount >= 20000) {
          this.payTypeId = ANNUALLY_PAID
        }
      }
    },
    loadStaffEmployee () {
      this.getStaffEmployee(this.id).then(() => {
        this.firstName = this.user.firstName
        this.lastName = this.user.lastName
        this.emailAddress = this.user.emailAddress
        this.securityRoles = this.user.securityRoles
      })
    },
    handleSaveEmployee () {
      if (this.$refs.form.validate()) {
        this.user.firstName = this.firstName
        this.user.lastName = this.lastName
        this.user.emailAddress = this.emailAddress
        this.user.securityRoles = this.securityRoles
        if (!this.isEdit) {
          this.saveStaffEmployee()
            .then(() => this.$router.push({ name: staffRoute.name }))
        } else {
          this.updateStaffEmployee()
            .then(() => this.$router.push({ name: staffRoute.name }))
        }
      }
    },
    cancel () {
      this.$router.push({ name: staffRoute.name })
    },
    newUserButtonClicked () {
      if (!this.isNewUser) {
        this.newUserOutlined = false
        this.existingUserOutlined = true
        this.userType = this.NEW_USER
        this.userId = null
        this.clearData()
      }
    },
    existingUserButtonClicked () {
      if (this.isNewUser) {
        this.newUserOutlined = true
        this.existingUserOutlined = false
        this.userType = this.EXISTING_USER
        this.clearData()
      }
    },
    async userIdChange (newValue) {
      this.userId = newValue
      await this.getUser({ id: this.userId }).then(async (user) => {
        this.firstName = user.firstName
        this.lastName = user.lastName
        this.emailAddress = user.emailAddress
        this.securityRoles = user.securityRoles
        await this.getCommissionsTiers({ userId: this.userId }).then((commissionTiers) => {
          this.commissionTiers = commissionTiers
        })
      })
    },
    clearData () {
      this.$refs.form.resetValidation()
      this.firstName = null
      this.lastName = null
      this.emailAddress = null
      this.securityRoles = []
      this.commissionTiers = []
    },
    isCorporateNameRequired () {
      return this.isC2C ? (!this.corporateName ? 'Corporate Name is Required' : true) : true
    },
    isEINRequired () {
      return this.isC2C ? (!this.ein ? 'EIN is Required' : true) : true
    },
    isFilledOut () {
      return this.employeeTypeId ? true : 'Please choose the consultant type'
    },
    endDateChangeHandler (endDate) {
      this.endDate = endDate
      if (!endDate) {
        this.active = true
        return
      }
      let tempEndDate = endDate.split('-')
      let today = new Date().setHours(0, 0, 0, 0)
      if (new Date(tempEndDate[0], parseInt(tempEndDate[1]) - 1, tempEndDate[2]) <= today) {
        this.active = false
      }
    }
  },
  async mounted () {
    await this.getSettings().then(async () => {
      this.$refs.form.resetValidation()
      if (this.isEdit) {
        this.loadStaffEmployee()
      } else {
        this.clearStaffEmployee()
        this.workedHoursAverage = this.payRateSettings.workedHoursAverageDefault
        this.active = true
      }
    })
  }
}
</script>
